import { useAppDataStore } from "@/mobx/appDataStore.jsx";
import { useEffect, useState } from "react";
import { observer } from "mobx-react";

const AVAILABLE_SCOPES = [
  { id: 'entertainment', label: 'Entertainment (Disney+)' },
  { id: 'snr', label: 'SNR' },
  { id: 'previsora', label: 'Previsora' },
  { id: 'tigogestion', label: 'Tigo Gestión' },
  { id: 'master', label: 'Master (Administrador del sistema)' },
];

export default observer(function AdminUsers(props) {
  const appDataStore = useAppDataStore();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const [editingScopes, setEditingScopes] = useState({});
  const [changingPasswordUserId, setChangingPasswordUserId] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newUserData, setNewUserData] = useState({
    email: "",
    name: "",
    phone: "",
    password: "",
    scopes: {}
  });
  const userScopes = appDataStore.user?.scopes || [];
  const hasMasterScope = Array.isArray(userScopes) && userScopes.includes('master');

  // Cargar usuarios al montar el componente
  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const data = await appDataStore.get('/users/list');
      setUsers(data.users || []);
    } catch (err) {
      console.error('Error al cargar usuarios:', err);
      alert('Error al cargar los usuarios: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (user) => {
    setEditingUserId(user.id);
    const scopesObj = {};
    user.scopes.forEach(scope => {
      scopesObj[scope] = true;
    });
    setEditingScopes(scopesObj);
  };

  const handleScopeChange = (scopeId) => {
    setEditingScopes(prev => ({
      ...prev,
      [scopeId]: !prev[scopeId]
    }));
  };

  const handleSaveScopes = async (userId) => {
    try {
      setLoading(true);
      const selectedScopes = Object.keys(editingScopes).filter(scope => editingScopes[scope]);

      await appDataStore.post('/users/update-scopes', {
        userId,
        scopes: selectedScopes,
      });

      alert('Scopes actualizados exitosamente');
      setEditingUserId(null);
      await loadUsers();
    } catch (err) {
      alert('Error al actualizar scopes: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setEditingUserId(null);
    setEditingScopes({});
  };

  const handleChangePassword = async (userId) => {
    if (!newPassword) {
      alert("La contraseña es requerida");
      return;
    }

    if (newPassword.length < 6) {
      alert("La contraseña debe tener al menos 6 caracteres");
      return;
    }

    if (newPassword !== confirmPassword) {
      alert("Las contraseñas no coinciden");
      return;
    }

    try {
      setLoading(true);
      await appDataStore.post("/users/change-password", {
        userId,
        newPassword,
      });

      alert("Contraseña actualizada exitosamente");
      setChangingPasswordUserId(null);
      setNewPassword("");
      setConfirmPassword("");
      await loadUsers();
    } catch (err) {
      alert("Error al cambiar contraseña: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelPassword = () => {
    setChangingPasswordUserId(null);
    setNewPassword("");
    setConfirmPassword("");
  };

  const handleNewUserScopeChange = (scopeId) => {
    setNewUserData(prev => ({
      ...prev,
      scopes: {
        ...prev.scopes,
        [scopeId]: !prev.scopes[scopeId]
      }
    }));
  };

  const handleCreateUser = async () => {
    if (!newUserData.email) {
      alert("El email es requerido");
      return;
    }

    if (!newUserData.email.includes("@")) {
      alert("Ingresa un email válido");
      return;
    }

    if (!newUserData.name) {
      alert("El nombre es requerido");
      return;
    }

    if (!newUserData.password) {
      alert("La contraseña es requerida");
      return;
    }

    if (newUserData.password.length < 6) {
      alert("La contraseña debe tener al menos 6 caracteres");
      return;
    }

    try {
      setLoading(true);
      const selectedScopes = Object.keys(newUserData.scopes).filter(scope => newUserData.scopes[scope]);

      await appDataStore.post("/users/create", {
        email: newUserData.email,
        name: newUserData.name,
        phone: newUserData.phone || null,
        password: newUserData.password,
        scopes: selectedScopes
      });

      alert("Usuario creado exitosamente");
      setShowCreateForm(false);
      setNewUserData({
        email: "",
        name: "",
        phone: "",
        password: "",
        scopes: {}
      });
      await loadUsers();
    } catch (err) {
      debugger
      alert("Error al crear usuario: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelCreate = () => {
    setShowCreateForm(false);
    setNewUserData({
      email: "",
      name: "",
      phone: "",
      password: "",
      scopes: {}
    });
  };

  if (!hasMasterScope) {
    return (
      <>
        <div className="p-2 max-w-7xl mx-auto text-black">
          <h2 className="text-2xl font-bold mb-4 text-gray-800">Gestión de Usuarios</h2>
          <div className="bg-red-50 border border-red-300 rounded-lg p-6 max-w-2xl">
            <div className="flex items-start gap-3">
              <span className="text-2xl">🔒</span>
              <div>
                <h3 className="text-lg font-bold text-red-800 mb-2">Acceso Denegado</h3>
                <p className="text-red-700">
                  No tienes permisos para acceder a la gestión de usuarios. Se requiere el scope <strong>Master</strong>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="p-2 max-w-7xl mx-auto text-black">
        <h2 className="text-2xl font-bold mb-4 text-gray-800">Gestión de Usuarios</h2>

        {/* Botón para crear usuario */}
        <div className="mb-6">
          <button
            onClick={() => setShowCreateForm(true)}
            disabled={loading}
            className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white rounded font-medium"
          >
            + Crear nuevo usuario
          </button>
        </div>

        {/* Modal para crear usuario */}
        {showCreateForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-lg">
              <h3 className="text-xl font-bold mb-4 text-gray-800">Crear nuevo usuario</h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                  <input
                    type="email"
                    placeholder="usuario@ejemplo.com"
                    value={newUserData.email}
                    onChange={(e) => setNewUserData(prev => ({...prev, email: e.target.value}))}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
                  <input
                    type="text"
                    placeholder="Juan Pérez"
                    value={newUserData.name}
                    onChange={(e) => setNewUserData(prev => ({...prev, name: e.target.value}))}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Teléfono (Opcional)</label>
                  <input
                    type="tel"
                    placeholder="+57 3001234567"
                    value={newUserData.phone}
                    onChange={(e) => setNewUserData(prev => ({...prev, phone: e.target.value}))}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Contraseña *</label>
                  <input
                    type="password"
                    placeholder="Mínimo 6 caracteres"
                    value={newUserData.password}
                    onChange={(e) => setNewUserData(prev => ({...prev, password: e.target.value}))}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-800 mb-3">Permisos (Scopes)</label>
                  <div className="space-y-2 bg-gray-50 p-3 rounded border border-gray-300">
                    {AVAILABLE_SCOPES.map((scope) => (
                      <label key={scope.id} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={newUserData.scopes[scope.id] || false}
                          onChange={() => handleNewUserScopeChange(scope.id)}
                          className="w-4 h-4 rounded border-gray-300 cursor-pointer"
                        />
                        <span className="text-sm text-gray-700">{scope.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={handleCreateUser}
                  disabled={loading}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white rounded font-medium"
                >
                  Crear Usuario
                </button>
                <button
                  onClick={handleCancelCreate}
                  disabled={loading}
                  className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-100 disabled:bg-gray-300 font-medium"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        )}

        {loading && users.length === 0 ? (
          <div className="text-center py-8 text-gray-600">Cargando usuarios...</div>
        ) : users.length === 0 ? (
          <div className="text-center py-8 text-gray-600">No hay usuarios</div>
        ) : (
          <div className="overflow-x-auto border border-gray-300 rounded-lg">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-100 border-b border-gray-300">
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Email</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Nombre</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Teléfono</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Scopes</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id} className="border-b border-gray-300 hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-800">{user.email}</td>
                    <td className="px-4 py-3 text-sm text-gray-800">{user.name}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{user.phone || '-'}</td>
                    <td className="px-4 py-3">
                      {editingUserId === user.id ? (
                        <div className="space-y-2">
                          {AVAILABLE_SCOPES.map((scope) => (
                            <label key={scope.id} className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={editingScopes[scope.id] || false}
                                onChange={() => handleScopeChange(scope.id)}
                                className="w-4 h-4 rounded border-gray-300 cursor-pointer"
                              />
                              <span className="text-sm text-gray-700">{scope.label}</span>
                            </label>
                          ))}
                        </div>
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          {user.scopes && user.scopes.length > 0 ? (
                            user.scopes.map((scope) => (
                              <span
                                key={scope}
                                className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-medium"
                              >
                                {AVAILABLE_SCOPES.find(s => s.id === scope)?.label || scope}
                              </span>
                            ))
                          ) : (
                            <span className="text-sm text-gray-500">Sin scopes</span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {editingUserId === user.id ? (
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleSaveScopes(user.id)}
                            disabled={loading}
                            className="px-3 py-1 bg-green-500 hover:bg-green-600 disabled:bg-gray-400 text-white rounded text-sm"
                          >
                            Guardar
                          </button>
                          <button
                            onClick={handleCancel}
                            disabled={loading}
                            className="px-3 py-1 bg-gray-500 hover:bg-gray-600 disabled:bg-gray-400 text-white rounded text-sm"
                          >
                            Cancelar
                          </button>
                        </div>
                      ) : changingPasswordUserId === user.id ? (
                        <div className="flex flex-col gap-2 min-w-xs">
                          <div>
                            <input
                              type="password"
                              placeholder="Nueva contraseña"
                              value={newPassword}
                              onChange={(e) => setNewPassword(e.target.value)}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                            />
                          </div>
                          <div>
                            <input
                              type="password"
                              placeholder="Confirmar contraseña"
                              value={confirmPassword}
                              onChange={(e) => setConfirmPassword(e.target.value)}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                            />
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleChangePassword(user.id)}
                              disabled={loading}
                              className="flex-1 px-3 py-1 bg-green-500 hover:bg-green-600 disabled:bg-gray-400 text-white rounded text-sm"
                            >
                              Confirmar
                            </button>
                            <button
                              onClick={handleCancelPassword}
                              disabled={loading}
                              className="flex-1 px-3 py-1 bg-gray-500 hover:bg-gray-600 disabled:bg-gray-400 text-white rounded text-sm"
                            >
                              Cancelar
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex gap-2 flex-wrap">
                          <button
                            onClick={() => handleEditClick(user)}
                            disabled={loading}
                            className="px-3 py-1 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-400 text-white rounded text-sm"
                          >
                            Editar
                          </button>
                          <button
                            onClick={() => setChangingPasswordUserId(user.id)}
                            disabled={loading}
                            className="px-3 py-1 bg-orange-500 hover:bg-orange-600 disabled:bg-gray-400 text-white rounded text-sm"
                          >
                            Contraseña
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
});
